/**
 * Image Optimization Utility
 * 
 * Resizes and compresses images to ensure they can be sent to AI services
 * without exceeding size limits. Uses canvas for client-side processing.
 */

import logger from './logger';

// Maximum dimensions for images sent to AI services
// Most AI APIs recommend images under 4MP (e.g., 2000x2000)
const MAX_DIMENSION = 2048;
const MAX_FILE_SIZE_MB = 4; // Target max size in MB
const JPEG_QUALITY_START = 0.9;
const JPEG_QUALITY_MIN = 0.5;

interface OptimizeOptions {
  maxDimension?: number;
  maxFileSizeMB?: number;
  jpegQuality?: number;
  preserveTransparency?: boolean;
}

interface OptimizeResult {
  dataUrl: string;
  originalSize: number;
  optimizedSize: number;
  wasOptimized: boolean;
  dimensions: { width: number; height: number };
}

/**
 * Optimize an image from a data URL or blob URL
 * Returns a compressed JPEG data URL (or PNG if transparency is preserved)
 */
export async function optimizeImage(
  imageUrl: string,
  options: OptimizeOptions = {}
): Promise<OptimizeResult> {
  const {
    maxDimension = MAX_DIMENSION,
    maxFileSizeMB = MAX_FILE_SIZE_MB,
    preserveTransparency = false,
  } = options;

  try {
    // If already a data URL, get the original size
    let originalSize = 0;
    let blob: Blob;

    if (imageUrl.startsWith('data:')) {
      // Estimate size from base64 (rough: base64 is ~33% larger than binary)
      const base64Part = imageUrl.split(',')[1] || '';
      originalSize = Math.floor(base64Part.length * 0.75);
      blob = dataUrlToBlob(imageUrl);
    } else {
      // Fetch the image
      const response = await fetch(imageUrl);
      blob = await response.blob();
      originalSize = blob.size;
    }

    const originalSizeMB = originalSize / (1024 * 1024);
    
    // Load image into a canvas
    const img = await loadImage(imageUrl);
    const originalWidth = img.width;
    const originalHeight = img.height;

    // Check if optimization is needed
    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
    const needsResize = img.width > maxDimension || img.height > maxDimension;
    const needsCompress = originalSize > maxSizeBytes;

    if (!needsResize && !needsCompress) {
      // No optimization needed, return original
      let dataUrl = imageUrl;
      if (!imageUrl.startsWith('data:')) {
        dataUrl = await blobToDataUrl(blob);
      }
      return {
        dataUrl,
        originalSize,
        optimizedSize: originalSize,
        wasOptimized: false,
        dimensions: { width: originalWidth, height: originalHeight },
      };
    }

    // Calculate new dimensions (maintain aspect ratio)
    let newWidth = originalWidth;
    let newHeight = originalHeight;
    
    if (needsResize) {
      const ratio = Math.min(maxDimension / originalWidth, maxDimension / originalHeight);
      newWidth = Math.floor(originalWidth * ratio);
      newHeight = Math.floor(originalHeight * ratio);
    }

    // Create canvas and draw resized image
    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Use high-quality image scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    // Convert to JPEG with progressive quality reduction until size is acceptable
    const mimeType = preserveTransparency ? 'image/png' : 'image/jpeg';
    let quality = JPEG_QUALITY_START;
    let resultDataUrl: string;
    let optimizedSize: number;

    do {
      resultDataUrl = canvas.toDataURL(mimeType, quality);
      // Estimate size from base64
      const base64Part = resultDataUrl.split(',')[1] || '';
      optimizedSize = Math.floor(base64Part.length * 0.75);
      
      logger.debug(`[optimizeImage] Quality ${quality.toFixed(2)}: ${(optimizedSize / (1024 * 1024)).toFixed(2)}MB`);
      
      // Reduce quality if still too large
      if (optimizedSize > maxSizeBytes && quality > JPEG_QUALITY_MIN) {
        quality -= 0.1;
      } else {
        break;
      }
    } while (quality >= JPEG_QUALITY_MIN);

    const optimizedSizeMB = optimizedSize / (1024 * 1024);
    logger.info(`[optimizeImage] Optimized: ${originalSizeMB.toFixed(2)}MB → ${optimizedSizeMB.toFixed(2)}MB (${newWidth}x${newHeight})`);

    return {
      dataUrl: resultDataUrl,
      originalSize,
      optimizedSize,
      wasOptimized: true,
      dimensions: { width: newWidth, height: newHeight },
    };
  } catch (error) {
    logger.error('[optimizeImage] Failed to optimize image:', error);
    throw error;
  }
}

/**
 * Load an image from a URL
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Handle CORS for external images
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(`Failed to load image: ${e}`));
    img.src = url;
  });
}

/**
 * Convert a data URL to a Blob
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

/**
 * Convert a Blob to a data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader result is not a string'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Check if an image needs optimization based on estimated size
 */
export function estimateImageSize(dataUrl: string): number {
  if (!dataUrl.startsWith('data:')) return 0;
  const base64Part = dataUrl.split(',')[1] || '';
  return Math.floor(base64Part.length * 0.75);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
